import React, { useState, useEffect } from 'react';
import { Select, Card, DatePicker, Spin } from 'antd';
import { LineChart, Line, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ArrowUpOutlined, ClockCircleOutlined, CheckCircleOutlined, UserOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import ApiService from '../service/api-service';

const { RangePicker } = DatePicker;

interface Product {
  id: string;
  name: string;
}

interface DashboardMetrics {
  totalSessions: number;
  completedSessions: number;
  avgSatisfactionScore: number;
  avgRecommendationScore: number;
  feedbackTrend: {
    date: string;
    count: number;
  }[];
  emotionalDistribution: {
    sentiment: string;
    count: number;
  }[];
  sentimetTrend: {
    sentiment: string;
    date: string;
    count: number;
  }[];
  satisfactionTrend: {
    date: string;
    avg: string;
  }[];
  recommendationTrend: {
    date: string;
    avg: string;
  }[];
}

const Dashboard: React.FC = () => {
  // Date range state
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);

  // Products state
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productError, setProductError] = useState<string | null>(null);

  // Dashboard metrics state
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  // Fetch products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      setProductError(null);
      try {
        const response = await ApiService.getProducts();
        setProducts(response.data.products || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProductError('Failed to load products');
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  // Fetch dashboard metrics when filters change
  useEffect(() => {
    const fetchMetrics = async () => {
      setIsLoadingMetrics(true);
      setMetricsError(null);
      try {
        const response = await ApiService.getDashboardMetrics({
          startDate: dateRange[0].startOf('day').toISOString(),
          endDate: dateRange[1].endOf('day').toISOString(),
          ...(selectedProduct !== 'all' && { productId: selectedProduct })
        });
        
        // Transform the response to match the DashboardMetrics interface
        const transformedMetrics: DashboardMetrics = {
          totalSessions: response.data.totalSessions,
          completedSessions: response.data.completedSessions,
          avgSatisfactionScore: response.data.avgSatisfactionScore,
          avgRecommendationScore: response.data.avgRecommendationScore,
          feedbackTrend: response.data.feedbackTrend,
          emotionalDistribution: response.data.emotionalDistribution,
          sentimetTrend: response.data.sentimetTrend,
          satisfactionTrend: response.data.satisfactionTrend,
          recommendationTrend: response.data.recommendationTrend
        };
        
        setMetrics(transformedMetrics);
      } catch (error) {
        console.error('Error fetching metrics:', error);
        setMetricsError('Failed to load dashboard metrics');
      } finally {
        setIsLoadingMetrics(false);
      }
    };

    fetchMetrics();
  }, [dateRange, selectedProduct]);

  // Handler for date range change
  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  // Handler for product change
  const handleProductChange = (value: string) => {
    setSelectedProduct(value);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Echo Analytics Dashboard</h1>
          <div className="flex items-center gap-4">
            <RangePicker
              defaultValue={dateRange}
              value={dateRange}
              onChange={handleDateRangeChange}
              allowClear={false}
              className="w-full sm:w-72"
              presets={[
                { label: 'Today', value: [dayjs(), dayjs()] },
                { label: 'Yesterday', value: [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
                { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day'), dayjs()] },
                { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day'), dayjs()] },
                { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                { 
                  label: 'Last Month', 
                  value: [
                    dayjs().subtract(1, 'month').startOf('month'),
                    dayjs().subtract(1, 'month').endOf('month')
                  ]
                },
              ]}
            />
            <Select
              value={selectedProduct}
              onChange={handleProductChange}
              className="w-full sm:w-48"
              loading={isLoadingProducts}
              status={productError ? 'error' : undefined}
              placeholder={isLoadingProducts ? 'Loading products...' : 'Select product'}
              options={[
                { value: 'all', label: 'All Products' },
                ...products.map(product => ({
                  value: product.id,
                  label: product.name
                }))
              ]}
            />
          </div>
        </div>
        
        {/* Core Feedback Metrics */}
        <Card 
          className="mb-6" 
          title={
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-blue-500"></span>
              Core Feedback Metrics
            </div>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg border border-gray-100">
              {isLoadingMetrics ? (
                <div className="h-full flex items-center justify-center">
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </div>
              ) : metricsError ? (
                <div className="h-full flex items-center justify-center text-red-500">
                  {metricsError}
                </div>
              ) : metrics ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-3">Total Feedback Sessions</h3>
                    <div className="flex items-center justify-center mb-4">
                      <div className="text-center">
                        <p className="text-5xl font-bold text-gray-800 mb-2">{metrics.totalSessions}</p>
                        <span className="text-green-500 flex items-center justify-center text-sm bg-green-50 px-3 py-1 rounded-full">
                          <ArrowUpOutlined className="mr-1" /> {((metrics.completedSessions / metrics.totalSessions) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm bg-gray-50 px-4 py-2 rounded-lg">
                      <span className="text-gray-600">Completed Sessions</span>
                      <span className="font-medium text-gray-800">{metrics.completedSessions}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm bg-gray-50 px-4 py-2 rounded-lg">
                      <span className="text-gray-600">Avg. Satisfaction Score</span>
                      <span className="font-medium text-gray-800">{metrics.avgSatisfactionScore.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm bg-gray-50 px-4 py-2 rounded-lg">
                      <span className="text-gray-600">Avg. Recommendation Score</span>
                      <span className="font-medium text-gray-800">{metrics.avgRecommendationScore.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-100">
              <h3 className="text-sm font-medium text-gray-600 mb-4">Feedback Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart 
                  data={metrics?.feedbackTrend || []}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => dayjs(date).format('MMM D')}
                    label={{ value: 'Date', position: 'insideBottom', offset: -10 }} 
                  />
                  <YAxis 
                    label={{ 
                      value: 'Number of Sessions', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }} 
                  />
                  <Tooltip 
                    labelFormatter={(date) => dayjs(date).format('MMMM D, YYYY')}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ paddingBottom: '20px' }}
                  />
                  <Line 
                    name="Sessions" 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#2196F3" 
                    strokeWidth={2} 
                    dot={false} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        {/* Sentiment & Emotion Analytics */}
        <Card className="mb-6" title={<div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-green-500"></span>User Sentiment & Emotion Analytics</div>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg border border-gray-100">
              <h3 className="text-sm font-medium text-gray-600 mb-4">Sentiment Trends Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart 
                  data={metrics?.sentimetTrend.reduce((acc, curr) => {
                    const existingDate = acc.find(item => item.date === curr.date);
                    if (existingDate) {
                      if (curr.sentiment === 'POSITIVE') {
                        existingDate.positive = curr.count;
                      } else if (curr.sentiment === 'NEGATIVE') {
                        existingDate.negative = curr.count;
                      }
                    } else {
                      acc.push({
                        date: curr.date,
                        positive: curr.sentiment === 'POSITIVE' ? curr.count : 0,
                        negative: curr.sentiment === 'NEGATIVE' ? curr.count : 0
                      });
                    }
                    return acc;
                  }, [] as Array<{
                    date: string;
                    positive: number;
                    negative: number;
                  }>).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) || []}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => dayjs(date).format('MMM D')}
                    label={{ value: 'Date', position: 'insideBottom', offset: -10 }} 
                  />
                  <YAxis 
                    label={{ 
                      value: 'Count', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }} 
                  />
                  <Tooltip 
                    labelFormatter={(date) => dayjs(date).format('MMMM D, YYYY')}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ paddingBottom: '20px' }}
                  />
                  <Line 
                    name="Positive" 
                    type="monotone" 
                    dataKey="positive"
                    stroke="#4CAF50" 
                    strokeWidth={2} 
                    dot={false} 
                  />
                  <Line 
                    name="Negative" 
                    type="monotone" 
                    dataKey="negative"
                    stroke="#FF5252" 
                    strokeWidth={2} 
                    dot={false} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-100">
              <h3 className="text-sm font-medium text-gray-600 mb-4">Emotion Distribution</h3>
              <div className="relative" style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <Pie
                      data={metrics?.emotionalDistribution.map(item => ({
                        name: item.sentiment,
                        value: item.count,
                        fill: item.sentiment === 'POSITIVE' ? '#4CAF50' : '#FF5252'
                      })) || []}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} ${(percent ? (percent * 100).toFixed(0) : 0)}%`}
                    />
                    <Tooltip formatter={(value) => `${value} sessions`} />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      wrapperStyle={{ 
                        paddingTop: '20px',
                        bottom: -10,
                        left: '50%',
                        transform: 'translateX(-50%)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </Card>

        {/* Satisfaction & Recommendation Trends */}
        <Card title={<div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-purple-500"></span>Satisfaction & Recommendation Trends</div>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg border border-gray-100">
              <h3 className="text-sm font-medium text-gray-600 mb-4">Satisfaction Trend Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart 
                  data={metrics?.satisfactionTrend || []}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => dayjs(date).format('MMM D')}
                    label={{ value: 'Date', position: 'insideBottom', offset: -10 }} 
                  />
                  <YAxis 
                    label={{ 
                      value: 'Average Score', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }} 
                  />
                  <Tooltip 
                    labelFormatter={(date) => dayjs(date).format('MMMM D, YYYY')}
                    formatter={(value) => Number(value).toFixed(1)}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ paddingBottom: '20px' }}
                  />
                  <Line 
                    name="Satisfaction" 
                    type="monotone" 
                    dataKey="avg" 
                    stroke="#673AB7" 
                    strokeWidth={2} 
                    dot={false} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-100">
              <h3 className="text-sm font-medium text-gray-600 mb-4">Recommendation Trend Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart 
                  data={metrics?.recommendationTrend || []}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => dayjs(date).format('MMM D')}
                    label={{ value: 'Date', position: 'insideBottom', offset: -10 }} 
                  />
                  <YAxis 
                    label={{ 
                      value: 'Average Score', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }} 
                  />
                  <Tooltip 
                    labelFormatter={(date) => dayjs(date).format('MMMM D, YYYY')}
                    formatter={(value) => Number(value).toFixed(1)}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ paddingBottom: '20px' }}
                  />
                  <Line 
                    name="Recommendations" 
                    type="monotone" 
                    dataKey="avg" 
                    stroke="#E91E63" 
                    strokeWidth={2} 
                    dot={false} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard; 